import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { FirmAccountantRow } from '@/core/models/firm-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { firmSettingsService } from '@/core/services/firmSettings';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { ClosePopup } from '@/kit/ClosePopup';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { openDialog } from '@/kit/dialog';
import { Icon } from '@/kit/Icon';
import { Paginator } from '@/kit/Paginator';
import { SelectionBar } from '@/kit/SelectionBar';
import { Tooltip } from '@/kit/Tooltip';
import { SearchField } from './SearchField';
import { useDebouncedDistinct } from './useDebouncedDistinct';
import './Accountants.scss';

const TOOLTIP =
  'Different accounting firms/contacts could be added from this section by the Accountant.\n' +
  '                    These Accountant Contact details could be then selected in Accountant’s Report section to be submitted with accounts.';

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 15;

/** Firm accountants master list (used by the accountant's report picker per client). */
export function Accountants() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<FirmAccountantRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const searchText = useRef('');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const getList = useCallback(() => {
    firmSettingsService.getAccountants(searchText.current.trim() || null).then(
      (list) => {
        if (!mounted.current) return;
        setRows(list);
        setPageIndex(0);
        setSelectedIds((ids) => new Set([...ids].filter((id) => list.some((row) => row.id === id))));
      },
      (error) => notifyError(error),
    );
  }, []);

  useEffect(() => {
    getList();
  }, [getList]);

  const onSearch = useDebouncedDistinct((value) => {
    searchText.current = value;
    getList();
  }, 300);

  const onPage = ({ pageIndex: pi, pageSize: ps }: { pageIndex: number; pageSize: number }) => {
    setPageIndex(pi);
    setPageSize(ps);
  };

  /** Slice of rows for the current page. */
  const pageRows = useMemo(
    () => rows.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [rows, pageIndex, pageSize],
  );

  const onAddClick = (row?: FirmAccountantRow) => {
    if (row) {
      navigate(matrixPath('/home/settings', 'addaccountants', { id: btoa(row.id) }));
    } else {
      navigate('/home/settings/addaccountants');
    }
  };

  const isAllSelected = pageRows.length > 0 && pageRows.every((row) => selectedIds.has(row.id));

  const toggleAll = (checked: boolean) =>
    setSelectedIds((ids) => {
      const next = new Set(ids);
      pageRows.forEach((row) => (checked ? next.add(row.id) : next.delete(row.id)));
      return next;
    });

  const toggleRow = (row: FirmAccountantRow, checked: boolean) =>
    setSelectedIds((ids) => {
      const next = new Set(ids);
      if (checked) next.add(row.id);
      else next.delete(row.id);
      return next;
    });

  const openDeleteClick = (row: FirmAccountantRow) =>
    confirmDelete(selectedIds.has(row.id) && selectedIds.size > 1 ? [...selectedIds] : [row.id], row.name);

  /** The ticked accountants (the bar), or the clicked one and any ticked with it. */
  const confirmDelete = (ids: string[], name?: string) => {
    const dialogRef = openDialog(ClosePopup, {
      data: { itemName: ids.length > 1 || !name ? 'Accountants' : name, count: ids.length },
    });

    dialogRef.afterClosed().then((confirmed) => {
      if (!confirmed) {
        return;
      }
      firmSettingsService.deleteAccountants(ids).then(
        () => {
          notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
          setSelectedIds((current) => {
            const next = new Set(current);
            ids.forEach((id) => next.delete(id));
            return next;
          });
          getList();
        },
        (error) => notifyError(error),
      );
    });
  };

  const columns: DataTableColumn<FirmAccountantRow>[] = [
    {
      id: 'name',
      header: (
        <>
          <Checkbox checked={isAllSelected} onChange={(event) => toggleAll(event.target.checked)} />
          Name
        </>
      ),
      cell: (row) => (
        <>
          <Checkbox checked={selectedIds.has(row.id)} onChange={(event) => toggleRow(row, event.target.checked)} />
          {row.name}
        </>
      ),
    },
    { id: 'address', header: 'Address', cell: (row) => row.address },
    { id: 'body', header: 'Body', cell: (row) => row.accountingBody },
    {
      id: 'actions',
      header: 'Action',
      cell: (row) => (
        <div className="d-flex align-items-center grid-actions">
          <Button title="Edit" onClick={() => onAddClick(row)}>
            <Icon name="edit" outlined />
          </Button>
          <Button title="Delete" onClick={() => openDeleteClick(row)}>
            <Icon name="delete_outline" outlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="form wp-100 settings-accountants">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Button className="action-button primary-button" onClick={() => onAddClick()}>
              <Icon name="add" />
              <span>Add Accountant</span>
            </Button>
            <SearchField onSearch={(value) => onSearch(value ?? '')} />
          </div>

          <div className="content-header-right mb-1">
            <div>
              <Tooltip title={TOOLTIP}>
                <Icon name="emoji_objects" className="text-yellow-200 highlight-icon" />
              </Tooltip>
            </div>
          </div>
        </div>
        <SelectionBar
          count={selectedIds.size}
          onDelete={() => confirmDelete([...selectedIds])}
          onClear={() => setSelectedIds(new Set())}
        />
        <div className="mat-table-wrapper mat-table-wrapper-pagination">
          <DataTable stickyHeader columns={columns} rows={pageRows} rowKey={(row) => row.id} />
        </div>
        {rows.length > 0 && (
          <Paginator
            length={rows.length}
            pageSize={pageSize}
            pageIndex={pageIndex}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            showFirstLastButtons
            onPage={onPage}
          />
        )}
      </div>
    </div>
  );
}
